/**
 * Integração de pagamento (v9 Etapa 2) — config da organização (API key/
 * ambiente/webhook, hoje só Asaas) + geração de cobrança por fatura. Contratos
 * do @erp/shared: FinanceIntegration (GET/PUT /billing/integrations/asaas) e
 * os campos provider-genéricos payment* do InvoiceSchema (paymentProvider,
 * externalPaymentId, paymentUrl, paymentStatus). `configured` é derivado:
 * apiKeyMasked != null.
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AsaasEnvironment,
  FinanceIntegration,
  FinanceIntegrationPutBody,
  Invoice,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { errorMessage as billingErrorMessage } from './billing-queries';

export type { AsaasEnvironment, FinanceIntegration, FinanceIntegrationPutBody };

const BILLING_KEY = ['erp', 'billing'] as const;
export const ASAAS_KEY = [...BILLING_KEY, 'asaas-integration'] as const;

// ── Config da integração ────────────────────────────────────────────────────

export function isAsaasConfigured(view: FinanceIntegration): boolean {
  return view.apiKeyMasked != null;
}

export const ASAAS_ENVIRONMENT_LABELS: Record<AsaasEnvironment, string> = {
  sandbox: 'Sandbox',
  production: 'Produção',
};

export function asaasIntegrationOptions() {
  return queryOptions({
    queryKey: ASAAS_KEY,
    queryFn: async ({ signal }) => {
      const res = await api<FinanceIntegration>('/billing/integrations/asaas', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function useUpdateAsaasIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: FinanceIntegrationPutBody) => {
      const res = await api<FinanceIntegration>('/billing/integrations/asaas', {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(ASAAS_KEY, data);
      void qc.invalidateQueries({ queryKey: ASAAS_KEY });
    },
  });
}

// ── Cobrança por fatura (rota provider-genérica) ────────────────────────────

export function useChargeInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: number) => {
      const res = await api<Invoice>(`/billing/invoices/${invoiceId}/charge`, {
        method: 'POST',
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BILLING_KEY }),
  });
}

// ── Chips de status por provedor ────────────────────────────────────────────

export interface PaymentStatusChip {
  label: string;
  variant: 'default' | 'success' | 'warning' | 'destructive' | 'info';
}

/** Nome amigável do provedor (fallback: o identificador cru). */
export function paymentProviderLabel(provider: string): string {
  return PROVIDER_LABELS[provider.toLowerCase()] ?? provider;
}

const PROVIDER_LABELS: Record<string, string> = {
  asaas: 'Asaas',
};

/** Mapa provedor → status (minúsculo) → chip pt-BR. */
const PROVIDER_STATUS_CHIPS: Record<string, Record<string, PaymentStatusChip>> = {
  asaas: {
    pending: { label: 'Aguardando pagamento', variant: 'warning' },
    received: { label: 'Pago no Asaas', variant: 'success' },
    confirmed: { label: 'Pago no Asaas', variant: 'success' },
    received_in_cash: { label: 'Pago no Asaas', variant: 'success' },
    overdue: { label: 'Vencida no Asaas', variant: 'destructive' },
    refunded: { label: 'Estornada no Asaas', variant: 'default' },
  },
};

/**
 * Chip do status da cobrança no provedor de pagamento — mapa por provedor
 * (hoje só `asaas`); provedor ou status desconhecido cai no cru com chip
 * neutro.
 */
export function paymentStatusChip(provider: string | null, status: string): PaymentStatusChip {
  const chip =
    provider !== null
      ? PROVIDER_STATUS_CHIPS[provider.toLowerCase()]?.[status.toLowerCase()]
      : undefined;
  return chip ?? { label: status, variant: 'default' };
}

// ── Erros pt-BR (códigos reais dos use-cases da API) ────────────────────────

const ERROR_LABELS: Record<string, string> = {
  already_charged: 'Esta fatura já tem uma cobrança gerada.',
  not_billed: 'Só faturas com status Faturado podem gerar cobrança.',
  invoice_not_found: 'Fatura não encontrada — recarregue a página e tente de novo.',
  no_payment_integration:
    'Configure (e ative) uma integração de pagamento em Configurações → Integrações antes.',
  asaas_error: 'O Asaas não respondeu como esperado — verifique a API key e tente de novo.',
  asaas_charge_failed: 'Não foi possível registrar a cobrança — tente de novo.',
  api_key_required: 'Informe a API key do Asaas na primeira configuração.',
};

export function asaasErrorMessage(err: unknown): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    const label = ERROR_LABELS[(err as { error: string }).error];
    if (label) return label;
  }
  return billingErrorMessage(err);
}
